import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, Clock, ShieldCheck } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ConsentSeen, useShopperInsightsApi } from '../../../common/hooks/use-shopper-insights-api.hook';
import type { ChatReturningNudgeSettings } from '../../../modules/vendor/settings/models/config.model';
import { consentStatus } from '../../../modules/vendor/settings/models/chat-settings';

type ConsentMode = NonNullable<ChatReturningNudgeSettings['consentMode']>;
const MODES: ConsentMode[] = ['auto', 'manual', 'not_required'];
const LABEL: Record<ConsentMode, string> = {
    auto: 'SHOPPER_UI.SETTINGS.RETURNING_CONSENT_AUTO',
    manual: 'SHOPPER_UI.SETTINGS.RETURNING_CONSENT_MANUAL',
    not_required: 'SHOPPER_UI.SETTINGS.RETURNING_CONSENT_NOT_REQUIRED',
};
const HINT: Record<ConsentMode, string> = {
    auto: 'SHOPPER_UI.SETTINGS.RETURNING_CONSENT_AUTO_HINT',
    manual: 'SHOPPER_UI.SETTINGS.RETURNING_CONSENT_MANUAL_HINT',
    not_required: 'SHOPPER_UI.SETTINGS.RETURNING_CONSENT_NOT_REQUIRED_HINT',
};

/**
 * Where the returning-visitor nudge gets the shopper's consent from.
 *
 * The default reads the answer the shopper already gave in the vendor's own
 * cookie banner, so the vendor installs nothing and their shoppers never see a
 * second banner. Under it, what the SDK actually found on their site — the
 * line that tells a vendor, before they wonder why nothing happens, that their
 * banner is one we cannot read.
 */
export const ReturningConsentField = ({
    mode,
    onChange,
}: {
    mode: ConsentMode | undefined;
    onChange: (mode: ConsentMode) => void;
}) => {
    const { t } = useTranslation();
    const api = useShopperInsightsApi();
    const [seen, setSeen] = useState<ConsentSeen | null>(null);
    const current: ConsentMode = mode || 'auto';

    useEffect(() => {
        let cancelled = false;
        api.getConsentSeen()
            .then((s) => { if (!cancelled) setSeen(s); })
            .catch(() => { if (!cancelled) setSeen(null); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const detected = consentStatus(seen);

    return (
        <div className="flex flex-col gap-3 rounded-lg bg-muted/40 px-3 py-3" data-testid="returning-consent">
            <p className="flex items-center gap-1.5 text-xs font-medium">
                <ShieldCheck className="size-3.5 text-muted-foreground" aria-hidden />
                {t('SHOPPER_UI.SETTINGS.RETURNING_CONSENT_MODE')}
            </p>

            <div role="radiogroup" aria-label={t('SHOPPER_UI.SETTINGS.RETURNING_CONSENT_MODE') as string} className="flex flex-col gap-1.5">
                {MODES.map((m) => (
                    <label key={m} className="flex cursor-pointer items-start gap-2 text-sm">
                        <input
                            type="radio"
                            name="returning-consent-mode"
                            id={`returning-consent-${m}`}
                            value={m}
                            checked={current === m}
                            onChange={() => onChange(m)}
                            className="mt-1"
                        />
                        <span>{t(LABEL[m])}</span>
                    </label>
                ))}
            </div>

            <p className="text-xs text-muted-foreground">{t(HINT[current])}</p>
            {current === 'manual' && (
                <code className="block overflow-x-auto rounded-md border bg-background px-2 py-1.5 text-[11px]">
                    ARdealy.setConsent(&#123; performance: true, personalization: true &#125;)
                </code>
            )}

            {current === 'auto' && (
                <p
                    className={cn(
                        'flex items-start gap-1.5 text-xs',
                        detected.status === 'detected' && 'text-emerald-700 dark:text-emerald-400',
                        detected.status === 'missing' && 'text-amber-700 dark:text-amber-400',
                        (detected.status === 'fallback' || detected.status === 'waiting') && 'text-muted-foreground',
                    )}
                    data-testid="returning-consent-detected"
                >
                    {detected.status === 'detected' && <CircleCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden />}
                    {detected.status === 'missing' && <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />}
                    {(detected.status === 'fallback' || detected.status === 'waiting') && <Clock className="mt-0.5 size-3.5 shrink-0" aria-hidden />}
                    {detected.status === 'detected' && t('SHOPPER_UI.SETTINGS.RETURNING_CONSENT_DETECTED', { name: detected.name })}
                    {detected.status === 'missing' && t('SHOPPER_UI.SETTINGS.RETURNING_CONSENT_MISSING')}
                    {detected.status === 'fallback' && t('SHOPPER_UI.SETTINGS.RETURNING_CONSENT_FALLBACK')}
                    {detected.status === 'waiting' && t('SHOPPER_UI.SETTINGS.RETURNING_CONSENT_WAITING')}
                </p>
            )}

            <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.SETTINGS.RETURNING_HOLDOUT')}</p>
        </div>
    );
};
